import { PushNotifications } from '@capacitor/push-notifications';
import { Dialog } from '@capacitor/dialog';
import { Capacitor } from '@capacitor/core';
import { messaging, getToken, onMessage } from './firebase';
import { toast } from '@/hooks/use-toast';
import { config } from './config';
import { navigationService, NotificationNavigationData } from './navigation';
import { storageService } from './storage';
import { authService } from './auth';
import { apiClient } from '@/shared/apiClient';
import { App as CapApp } from '@capacitor/app';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';

// Unified permission type for the app
type AppPermission = 'granted' | 'denied' | 'default';

// Mapper function to convert native permission to app permission
function mapNativeToAppPermission(
  receive: 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale'
): AppPermission {
  return receive === 'prompt' || receive === 'prompt-with-rationale' ? 'default' : receive;
}

// Cross-platform function to open notification settings
export async function openNotificationSettings(): Promise<void> {
  try {
    if (Capacitor.getPlatform() === 'android') {
      // Try direct notification settings (API 26+). Fallback: app details page.
      try {
        await NativeSettings.open({ 
          optionAndroid: AndroidSettings.ApplicationDetails,
          optionIOS: IOSSettings.App 
        });
      } catch {
        await NativeSettings.open({ 
          optionAndroid: AndroidSettings.ApplicationDetails,
          optionIOS: IOSSettings.App 
        });
      }
    } else {
      // iOS: opens the app's settings page
      await NativeSettings.open({ 
        optionAndroid: AndroidSettings.ApplicationDetails,
        optionIOS: IOSSettings.App 
      });
    }
  } catch (error) {
    console.error('Failed to open notification settings:', error);
  }
}

export interface NotificationData {
  title: string;
  body: string;
  data?: Record<string, any>;
}

class NotificationService {
  private fcmToken: string | null = null;
  private isInitialized = false;
  private tokenPromise: Promise<string | null> | null = null;
  private isRequestingPermissions = false;
  private listenersRegistered = false;
  private appStateListeners: (() => void)[] = [];
  private onMessageReceivedCallbacks: Array<(data: any) => void> = [];
  private appStateListenerHandle: any = null;
  private pushNotificationListeners: any[] = [];

  async initialize(requestPermissions: boolean = false): Promise<string | null> {
    // First, check if we have a valid FCM token in storage
    const storedToken = await this.getFcmTokenFromStorage();
    const storedPermission = await this.getStoredPermissionStatus();
    
    console.log('Initialize check - stored token:', !!storedToken, 'stored permission:', storedPermission);
    
    // If we have a stored token and permission is granted, use it
    if (storedToken && storedPermission === 'granted') {
      console.log('Using stored FCM token and permission status');
      this.fcmToken = storedToken;
      this.isInitialized = true;
      
      // Always set up listeners even when using stored token
      if (Capacitor.isNativePlatform()) {
        console.log('Setting up Capacitor listeners for stored token');
        this.setupCapacitorListeners();
      }
      
      return this.fcmToken;
    }
    
    // If we have stored permission as granted but no token, try to get token
    if (storedPermission === 'granted' && !storedToken) {
      console.log('Permission granted but no stored token, attempting to get token');
      // Continue with initialization to get token
    }
    
    // If we have stored permission as denied, don't initialize
    if (storedPermission === 'denied' && !requestPermissions) {
      console.log('Permission denied in storage, skipping initialization');
      return null;
    }

    if (this.isInitialized && this.fcmToken) {
      return this.fcmToken;
    }

    // If we're already initializing, return the existing promise
    if (this.tokenPromise) {
      return this.tokenPromise;
    }

    // For login flow, if we're already initialized but no token, don't re-initialize
    // This prevents the retry loop during login
    if (this.isInitialized && !this.fcmToken && !requestPermissions) {
      console.log('Notifications: Already initialized but no token, skipping re-initialization for login');
      return null;
    }

    try {
      // Reset initialization state to ensure fresh start
      this.isInitialized = false;
      this.fcmToken = null;
      
      if (Capacitor.isNativePlatform()) {
        // Use Capacitor Push Notifications for native platforms
        this.tokenPromise = this.initializeCapacitorPush(requestPermissions);
      } else {
        // Use Firebase for web platform
        this.tokenPromise = this.initializeFirebasePush(requestPermissions);
      }
      
      const token = await this.tokenPromise;
      this.isInitialized = true;
      this.tokenPromise = null;
      
      // Set up app state detection for permission changes
      this.setupAppStateDetection();
      
      return token;
    } catch (error) {
      console.error('Failed to initialize notifications:', error);
      this.tokenPromise = null;
      return null;
    }
  }

  private async initializeCapacitorPush(requestPermissions: boolean = false): Promise<string | null> {
    return new Promise(async (resolve, reject) => {
      // Set up a timeout to avoid waiting indefinitely
      const timeout = setTimeout(() => {
        console.warn('FCM token timeout - proceeding without token');
        resolve(null);
      }, 5000); // 5 second timeout for faster login

      try {
        // Check permission status first with error handling
        let permission;
        try {
          permission = await PushNotifications.checkPermissions();
          console.log('Initial permission check result:', permission);
        } catch (error) {
          console.error('Error checking initial permissions:', error);
          clearTimeout(timeout);
          resolve(null);
          return;
        }
        
        if (permission.receive === 'granted') {
          // Create notification channel (Android)
          await this.createNotificationChannel();
          
          // Register with Apple / Google to receive push via APNS/FCM
          await PushNotifications.register();
          
          // Set up listeners with promise resolution
          this.setupCapacitorListeners(resolve, reject, timeout);
        } else {
          if (requestPermissions) {
            // Request permission with explanation dialog (only during initialization)
            console.log('Requesting push notification permissions for app functionality...');
            const permissionResult = await this.requestPermissionWithExplanation();
            console.log('Permission result from explanation dialog:', permissionResult);
            
            if (permissionResult === 'granted') {
              console.log('Permissions granted, registering for push notifications...');
              // Create notification channel (Android)
              await this.createNotificationChannel();
              
              // Register with Apple / Google to receive push via APNS/FCM
              await PushNotifications.register();
              
              // Set up listeners with promise resolution
              this.setupCapacitorListeners(resolve, reject, timeout);
            } else {
              clearTimeout(timeout);
              console.log('Push notifications not granted, skipping initialization');
              resolve(null);
            }
          } else {
            // Don't request permissions (for login flow)
            clearTimeout(timeout);
            console.log('Push notifications not granted and not requesting, skipping initialization');
            resolve(null);
          }
        }
      } catch (error) {
        console.error('Error in initializeCapacitorPush:', error);
        clearTimeout(timeout);
        resolve(null);
      }
    });
  }

  private async initializeFirebasePush(requestPermissions: boolean = false): Promise<string | null> {
    try {
      // Check if messaging is available (only on web platform)
      if (!messaging) {
        console.warn('Firebase messaging not available on this platform');
        return null;
      }

      // Check permission status first
      if (Notification.permission === 'granted') {
        // Get FCM token
        this.fcmToken = await getToken(messaging, {
          vapidKey: config.vapidKey
        });
        
        // Set up listeners
        this.setupFirebaseListeners();
        
        return this.fcmToken;
      } else {
        if (requestPermissions) {
          // Request permission with explanation dialog (only during initialization)
          console.log('Requesting notification permissions for app functionality...');
          const permission = await this.requestPermissionWithExplanation();
          
          if (permission === 'granted') {
            // Get FCM token
            this.fcmToken = await getToken(messaging, {
              vapidKey: config.vapidKey
            });
            
            // Set up listeners
            this.setupFirebaseListeners();
            
            // Update FCM token on server
            if (this.fcmToken) {
              await this.updateFcmTokenOnServer(this.fcmToken);
            }
            
            return this.fcmToken;
          } else {
            console.log('Notification permission not granted, skipping Firebase initialization');
            return null;
          }
        } else {
          // Don't request permissions (for login flow)
          console.log('Notification permission not granted and not requesting, skipping Firebase initialization');
          return null;
        }
      }
    } catch (error) {
      console.error('Firebase push initialization failed:', error);
      return null;
    }
  }

  private setupCapacitorListeners(resolve?: (token: string | null) => void, reject?: (error: any) => void, timeout?: NodeJS.Timeout): void {
    // Clean up existing listeners first to prevent duplicates
    this.cleanupPushNotificationListeners();
    
    // Ensure notification channel is created before setting up listeners
    this.createNotificationChannel();
    
    // Registration success
    const registrationListener = PushNotifications.addListener('registration', async (token) => {
      this.fcmToken = token.value;
      console.log('Push registration success:', token.value);
      
      // Save FCM token to storage
      await this.saveFcmTokenToStorage(this.fcmToken);
      
      // Save permission status as granted since we got a token
      await this.savePermissionStatus('granted');
      
      // Notify listeners that permission status changed
      console.log('Notifying', this.appStateListeners.length, 'listeners of permission change after token received');
      this.appStateListeners.forEach(listener => listener());
      
      // Update FCM token on server whenever we get a new token
      if (this.fcmToken) {
        await this.updateFcmTokenOnServer(this.fcmToken);
      }
      
      // Resolve the promise if we have a resolve callback
      if (resolve) {
        clearTimeout(timeout);
        resolve(token.value);
      }
    });
    this.pushNotificationListeners.push(registrationListener);

    // Registration error
    const registrationErrorListener = PushNotifications.addListener('registrationError', (error) => {
      console.error('Error on registration:', error.error);
      
      // Reject the promise if we have a reject callback
      if (reject) {
        clearTimeout(timeout);
        reject(error);
      }
    });
    this.pushNotificationListeners.push(registrationErrorListener);

    // Push notification received (app in foreground)
    const pushReceivedListener = PushNotifications.addListener('pushNotificationReceived', async (notification) => {
      console.log('Push received in foreground:', notification);
      
      // Notify registered callbacks about the message (for task refresh)
      this.notifyMessageReceived(notification.data);
      
      // Show notification as alert/toast with data
      await this.showNotification(
        notification.title || 'Notification', 
        notification.body || '', 
        notification.data
      );
    });
    this.pushNotificationListeners.push(pushReceivedListener);

    // Push notification action clicked (app was closed or in background)
    const pushActionListener = PushNotifications.addListener('pushNotificationActionPerformed', async (notification) => {
      console.log('🔔 Push action performed:', notification);
      console.log('🔔 Notification data:', notification.notification?.data);
      console.log('🔔 App state when notification clicked:', document.hidden ? 'background' : 'foreground');
      
      try {
        // Check auth, but do not block navigation – user may land on login if needed
        const isAuthenticated = await authService.isSessionValid();
        console.log('User authenticated status:', isAuthenticated);
        
        // Handle navigation when notification is clicked
        // Always call navigation service - it will determine the correct path based on data
        const raw = notification.notification.data ?? {};
        const navigationData: NotificationNavigationData = {
          ...raw,
          title: (raw as { title?: string }).title ?? notification.notification.title,
          body: (raw as { body?: string }).body ?? notification.notification.body,
        };
        
        console.log('Navigation data prepared:', navigationData);
        
        // Ensure navigation service is initialized
        navigationService.initialize();
        
        // Set a timeout to ensure navigation happens even if there are issues
        const navigationTimeout = setTimeout(() => {
          console.log('Navigation timeout reached, ensuring app is navigated');
          // Force navigation to tasks list as fallback
          if (navigationService['navigationCallback']) {
            navigationService['navigationCallback']('/shifts');
          }
        }, 3000); // 3 second timeout
        
        navigationService.handleNotificationNavigation(navigationData);
        
        // Clear timeout if navigation succeeds quickly
        setTimeout(() => {
          clearTimeout(navigationTimeout);
        }, 1000);
      } catch (error) {
        console.error('Error handling notification action:', error);
      }
    });
    this.pushNotificationListeners.push(pushActionListener);
    
    console.log('Push notification listeners set up, total listeners:', this.pushNotificationListeners.length);
  }

  // Clean up push notification listeners
  private cleanupPushNotificationListeners(): void {
    console.log('Cleaning up push notification listeners, current count:', this.pushNotificationListeners.length);
    this.pushNotificationListeners.forEach(listener => {
      try {
        listener.remove();
      } catch (error) {
        console.error('Error removing push notification listener:', error);
      }
    });
    this.pushNotificationListeners = [];
  }

  private setupFirebaseListeners(): void {
    // Check if messaging is available before setting up listeners
    if (!messaging) {
      console.warn('Firebase messaging not available, skipping listener setup');
      return;
    }

    // Handle foreground messages
    onMessage(messaging, async (payload) => {
      console.log('Message received in foreground:', payload);
      
      // Notify registered callbacks about the message
      this.notifyMessageReceived(payload.data);
      
      const notification = payload.notification;
      if (notification) {
        await this.showNotification(
          notification.title || 'Notification', 
          notification.body || '', 
          payload.data
        );
      }
    });
  }

  private async showNotification(title: string, body: string, data?: Record<string, any>): Promise<void> {
    // Always show a custom in-app notification alert; navigation will be handled
    // by the React layer and may route to login if the user is not authenticated.
    this.showCustomAlert(title, body, data);
  }

  private showCustomAlert(title: string, body: string, data?: Record<string, any>): void {
    // Dispatch a custom event that the React app can listen to
    const event = new CustomEvent('showNotification', {
      detail: { title, body, data }
    });
    window.dispatchEvent(event);
  }

  private async showNativeAlert(title: string, body: string, data?: Record<string, any>): Promise<void> {
    console.log('Native notification received:', { title, body, data });
    
    try {
      // Show a native alert dialog
      await Dialog.alert({
        title: title,
        message: body,
        buttonTitle: data ? 'View' : 'OK',
      });
      
      // If we have data, handle navigation (user clicked the button)
      if (data) {
        navigationService.handleNotificationNavigation({
          ...data,
        } as NotificationNavigationData);
      }
    } catch (error) {
      console.error('Failed to show native alert:', error);
    }
  }

  getToken(): string | null {
    return this.fcmToken;
  }

  getPlatform(): string {
    if (Capacitor.isNativePlatform()) {
      return Capacitor.getPlatform();
    }
    return 'web';
  }

  async getDeviceId(): Promise<string> {
    // Try to get device ID from storage first
    let deviceId = await storageService.getDeviceId();
    
    if (!deviceId) {
      // Generate a simple device ID for web
      // In a real app, you might want to use a more sophisticated approach
      deviceId = 'web_' + Math.random().toString(36).substr(2, 9);
      await storageService.setDeviceId(deviceId);
    }
    
    return deviceId;
  }

  /**
   * Clear notification state when user logs out
   */
  clearNotifications(): void {
    this.fcmToken = null;
    this.isInitialized = false;
    this.isRequestingPermissions = false;
    
    // Clean up app state listener
    if (this.appStateListenerHandle) {
      this.appStateListenerHandle.remove();
      this.appStateListenerHandle = null;
    }
    
    // Clean up push notification listeners
    this.cleanupPushNotificationListeners();
    
    // Clear all permission change listeners
    this.appStateListeners = [];
    
    // Clear all message received callbacks
    this.onMessageReceivedCallbacks = [];
    
    // Clear permission storage
    this.clearPermissionStorage();
    
    console.log('Notifications: Cleared notification state');
  }

  /**
   * Force re-initialization of notifications (useful after logout)
   */
  async forceReinitialize(): Promise<string | null> {
    console.log('Notifications: Force re-initializing...');
    
    // Complete cleanup first
    this.clearNotifications();
    
    // Reset all state
    this.isInitialized = false;
    this.fcmToken = null;
    this.tokenPromise = null;
    this.isRequestingPermissions = false;
    this.listenersRegistered = false;
    
    // Wait a bit to ensure cleanup is complete
    await new Promise(resolve => setTimeout(resolve, 100));
    
    return this.initialize();
  }

  /**
   * Check if notification permissions are granted with robust error handling
   */
  async checkPermissionStatus(): Promise<AppPermission> {
    if (Capacitor.isNativePlatform()) {
      try {
        // Wait for Capacitor bridge to be ready before checking permissions
        await this.waitForCapacitorReady();
        
        const permission = await PushNotifications.checkPermissions();
        console.log('Permission check result:', permission);
        const appPermission = mapNativeToAppPermission(permission.receive);
        
        // Save permission status to storage
        await this.savePermissionStatus(appPermission);
        
        return appPermission;
      } catch (error) {
        console.error('Error checking permissions:', error);
        
        // Check if this is the specific NullPointerException we're seeing
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage && (
          errorMessage.includes('NullPointerException') ||
          errorMessage.includes('Bridge.getPermissionStates') ||
          errorMessage.includes('InvocationTargetException')
        )) {
          console.log('Bridge not ready for permission check, returning denied');
          return 'denied';
        }
        
        // Return 'denied' as safe fallback for any other error
        return 'denied';
      }
    } else {
      // For web platform
      if ('Notification' in window) {
        return Notification.permission as AppPermission;
      }
      return 'denied';
    }
  }

  /**
   * Wait for Capacitor bridge to be ready
   */
  private async waitForCapacitorReady(): Promise<void> {
    return new Promise((resolve) => {
      let attempts = 0;
      const maxAttempts = 50; // 5 seconds max wait
      
      const checkReady = () => {
        attempts++;
        
        try {
          // Check if Capacitor is available and ready
          if (typeof window !== 'undefined' && 
              (window as any).Capacitor && 
              (window as any).Capacitor.isNativePlatform() &&
              (window as any).Capacitor.Plugins &&
              (window as any).Capacitor.Plugins.PushNotifications) {
            console.log('Capacitor bridge is ready');
            resolve();
            return;
          }
        } catch (error) {
          console.log('Error checking Capacitor readiness:', error);
        }
        
        if (attempts >= maxAttempts) {
          console.warn('Capacitor bridge not ready after 5 seconds, proceeding anyway');
          resolve();
          return;
        }
        
        // Wait 100ms and try again
        setTimeout(checkReady, 100);
      };
      
      // Start checking immediately
      checkReady();
    });
  }

  /**
   * Storage-based permission management
   */
  private async savePermissionStatus(status: AppPermission): Promise<void> {
    try {
      await storageService.set('notification_permission_status', status);
      console.log('Saved permission status to storage:', status);
    } catch (error) {
      console.error('Failed to save permission status:', error);
    }
  }

  private async getPermissionStatusFromStorage(): Promise<AppPermission | null> {
    try {
      const status = await storageService.get('notification_permission_status');
      console.log('Retrieved permission status from storage:', status);
      return status as AppPermission | null;
    } catch (error) {
      console.error('Failed to get permission status from storage:', error);
      return null;
    }
  }

  private async saveFcmTokenToStorage(token: string): Promise<void> {
    try {
      await storageService.set('fcm_token', token);
      console.log('Saved FCM token to storage');
    } catch (error) {
      console.error('Failed to save FCM token to storage:', error);
    }
  }

  private async getFcmTokenFromStorage(): Promise<string | null> {
    try {
      const token = await storageService.get('fcm_token');
      console.log('Retrieved FCM token from storage');
      return token as string | null;
    } catch (error) {
      console.error('Failed to get FCM token from storage:', error);
      return null;
    }
  }

  private async clearPermissionStorage(): Promise<void> {
    try {
      await storageService.remove('notification_permission_status');
      await storageService.remove('fcm_token');
      console.log('Cleared permission storage');
    } catch (error) {
      console.error('Failed to clear permission storage:', error);
    }
  }

  /**
   * Get permission status from storage (fast, no bridge calls)
   */
  async getStoredPermissionStatus(): Promise<AppPermission> {
    const storedStatus = await this.getPermissionStatusFromStorage();
    return storedStatus || 'default';
  }

  /**
   * Check if we have a valid FCM token in storage
   */
  async hasValidFcmTokenInStorage(): Promise<boolean> {
    const token = await this.getFcmTokenFromStorage();
    return !!token;
  }

  /**
   * Request notification permissions without initializing the full service
   */
  async requestPermissionOnly(): Promise<AppPermission> {
    if (Capacitor.isNativePlatform()) {
      try {
        // Wait for Capacitor bridge to be ready before requesting permissions
        await this.waitForCapacitorReady();
        
        const permission = await PushNotifications.requestPermissions();
        console.log('Permission request result:', permission);
        const appPermission = mapNativeToAppPermission(permission.receive);
        
        // Save permission status to storage
        await this.savePermissionStatus(appPermission);
        
        return appPermission;
      } catch (error) {
        console.error('Error requesting permissions:', error);
        
        // Check if this is the specific NullPointerException we're seeing
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage && (
          errorMessage.includes('NullPointerException') ||
          errorMessage.includes('Bridge.getPermissionStates') ||
          errorMessage.includes('InvocationTargetException')
        )) {
          console.log('Bridge not ready for permission request, returning denied');
          return 'denied';
        }
        
        // Return 'denied' as safe fallback for any other error
        return 'denied';
      }
    } else {
      // For web platform
      if ('Notification' in window) {
        return await Notification.requestPermission() as AppPermission;
      }
      return 'denied';
    }
  }

  /**
   * Request permissions and initialize notifications (for banner)
   */
  async requestPermissionsAndInitialize(): Promise<string | null> {
    console.log('Requesting permissions and initializing notifications...');
    
    if (Capacitor.isNativePlatform()) {
      try {
        // Check current permission status first with error handling
        let permission;
        try {
          permission = await PushNotifications.checkPermissions();
          console.log('Current permission status:', permission);
        } catch (error) {
          console.error('Error checking current permissions:', error);
          return null;
        }
        
        if (permission.receive === 'granted') {
          console.log('Permissions already granted, registering for push notifications...');
          
          // Save permission status to storage
          await this.savePermissionStatus('granted');
          
          // Create notification channel (Android)
          await this.createNotificationChannel();
          
          // Register with Apple / Google to receive push via APNS/FCM
          await PushNotifications.register();
          
          // Set up listeners to get the token
          return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
              console.warn('FCM token timeout - proceeding without token');
              resolve(null);
            }, 5000); // 5 second timeout for faster login
            
            this.setupCapacitorListeners(resolve, reject, timeout);
          });
        } else {
          // Request native permissions directly (no custom dialog)
          console.log('Requesting native permissions...');
          let permissionResult;
          try {
            permissionResult = await PushNotifications.requestPermissions();
            console.log('Native permission result:', permissionResult);
          } catch (error) {
            console.error('Error requesting native permissions:', error);
            return null;
          }
          
          const appPerm = mapNativeToAppPermission(permissionResult.receive);
          
          if (appPerm === 'granted') {
            console.log('Permissions granted, registering for push notifications...');
            
            // Save permission status to storage
            await this.savePermissionStatus('granted');
            
            // Create notification channel (Android)
            await this.createNotificationChannel();
            
            // Register with Apple / Google to receive push via APNS/FCM
            await PushNotifications.register();
            
            // Set up listeners to get the token
            return new Promise((resolve, reject) => {
              const timeout = setTimeout(() => {
                console.warn('FCM token timeout - proceeding without token');
                resolve(null);
              }, 5000); // 5 second timeout for faster login
              
              this.setupCapacitorListeners(resolve, reject, timeout);
            });
          } else {
            console.log('Push notifications not granted:', permissionResult);
            // Save denied status to storage
            await this.savePermissionStatus('denied');
            return null;
          }
        }
      } catch (error) {
        console.error('Error in requestPermissionsAndInitialize:', error);
        return null;
      }
    } else {
      // For web platform
      if ('Notification' in window) {
        const permission = await Notification.requestPermission();
        console.log('Web permission result:', permission);
        
        if (permission === 'granted') {
          // Save permission status to storage
          await this.savePermissionStatus('granted');
          
          // Get FCM token
          this.fcmToken = await getToken(messaging, {
            vapidKey: config.vapidKey
          });
          
          // Save FCM token to storage
          if (this.fcmToken) {
            await this.saveFcmTokenToStorage(this.fcmToken);
          }
          
          // Set up listeners
          this.setupFirebaseListeners();
          
          // Update FCM token on server
          if (this.fcmToken) {
            await this.updateFcmTokenOnServer(this.fcmToken);
          }
          
          return this.fcmToken;
        } else {
          // Save denied status to storage
          await this.savePermissionStatus('denied');
        }
      }
      return null;
    }
  }

  /**
   * Update FCM token on the server
   */
  async updateFcmTokenOnServer(fcmToken: string): Promise<void> {
    try {
      // Check if user is authenticated before updating FCM token
      const isAuthenticated = await authService.isSessionValid();
      if (!isAuthenticated) {
        console.log('User not authenticated, skipping FCM token update on server');
        return;
      }
      
      const deviceId = await this.getDeviceId();
      console.log('Updating FCM token on server:', { deviceId, fcmToken });
      
      await apiClient.put(`/api/employee/devices/${deviceId}/fcm-token`, {
        fcm_token: fcmToken
      });
      
      console.log('FCM token updated successfully on server');
    } catch (error) {
      console.error('Failed to update FCM token on server:', error);
      // Don't throw error - this shouldn't block the user experience
    }
  }

  /**
   * Update FCM token on server if user is authenticated (called after login)
   */
  async updateFcmTokenIfAuthenticated(): Promise<void> {
    if (this.fcmToken) {
      console.log('Updating FCM token on server after authentication');
      await this.updateFcmTokenOnServer(this.fcmToken);
    }
  }

  /**
   * Register device with backend only when not already registered for this employee (backfill).
   * Reads client-side flag; if device is already registered for this employee, does nothing.
   * Otherwise gets FCM token, calls PUT /api/employee/devices/:id/fcm-token, and sets flag on success.
   */
  async ensureDeviceRegisteredIfNeeded(employeeId: number): Promise<void> {
    try {
      const registeredFor = await storageService.getDeviceRegisteredEmployeeId();
      if (registeredFor === employeeId) {
        console.log('Device already registered for this employee, skipping PUT');
        return;
      }
      let token = this.fcmToken ?? this.getToken();
      if (!token) {
        await this.initialize(false);
        token = this.fcmToken ?? this.getToken();
      }
      if (!token) {
        console.log('No FCM token available for device registration');
        return;
      }
      await this.updateFcmTokenOnServer(token);
      await storageService.setDeviceRegisteredEmployeeId(employeeId);
      console.log('Device registered for employee', employeeId);
    } catch (error) {
      console.error('ensureDeviceRegisteredIfNeeded failed:', error);
      // Do not set the flag so we retry later
    }
  }

  /**
   * Open app settings for notification permissions (cross-platform)
   */
  async openAppSettings(): Promise<void> {
    await openNotificationSettings();
  }

  /**
   * Create notification channel (Android)
   */
  async createNotificationChannel(): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      try {
        await PushNotifications.createChannel({
          id: 'sawadone_notifications',
          name: 'TightShip Notifications',
          description: 'Notifications for tasks and messages',
          importance: 4, // High importance
          visibility: 1, // Public visibility
          sound: 'default',
          lights: true,
          vibration: true,
        });
        console.log('Notification channel created with ID: sawadone_notifications');
      } catch (error) {
        console.error('Failed to create notification channel:', error);
      }
    }
  }

  /**
   * Set up app state change detection to handle permission changes
   */
  private setupAppStateDetection(): void {
    if (Capacitor.isNativePlatform()) {
      // Remove existing listener if any
      if (this.appStateListenerHandle) {
        this.appStateListenerHandle.remove();
        this.appStateListenerHandle = null;
      }
      
      this.appStateListenerHandle = CapApp.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          console.log('App became active, checking notification permissions...');
          await this.handleAppResume();
        }
      });
    }
  }

  /**
   * Handle app resume - check if permissions changed and get FCM token if needed
   */
  private async handleAppResume(): Promise<void> {
    try {
      // Use storage-based permission check (fast, no bridge calls)
      const currentPermission = await this.getStoredPermissionStatus();
      console.log('Current permission status on app resume (from storage):', currentPermission);
      
      if (currentPermission === 'granted' && !this.fcmToken) {
        console.log('Permissions granted but no FCM token, initializing...');
        const token = await this.initialize(false); // Don't request permissions, just get token
        if (token) {
          console.log('FCM token obtained after app resume:', token);
          // Notify listeners that permission status changed
          console.log('Notifying', this.appStateListeners.length, 'listeners of permission change');
          this.appStateListeners.forEach(listener => listener());
        }
      } else if (currentPermission === 'granted' && this.fcmToken) {
        // Permissions are granted and we have a token, just notify listeners
        console.log('Permissions granted with token, notifying', this.appStateListeners.length, 'listeners');
        this.appStateListeners.forEach(listener => listener());
      } else {
        // Permissions are not granted, notify listeners anyway to update banner
        console.log('Permissions not granted, notifying', this.appStateListeners.length, 'listeners to update banner');
        this.appStateListeners.forEach(listener => listener());
      }
    } catch (error) {
      console.error('Error handling app resume:', error);
    }
  }

  /**
   * Add listener for permission status changes
   */
  addPermissionChangeListener(listener: () => void): void {
    this.appStateListeners.push(listener);
    console.log('Added permission change listener, total listeners:', this.appStateListeners.length);
  }

  /**
   * Remove listener for permission status changes
   */
  removePermissionChangeListener(listener: () => void): void {
    this.appStateListeners = this.appStateListeners.filter(l => l !== listener);
  }

  /**
   * Request notification permissions with a custom explanation dialog
   */
  async requestPermissionWithExplanation(): Promise<AppPermission> {
    // Prevent multiple simultaneous permission requests
    if (this.isRequestingPermissions) {
      console.log('Permission request already in progress, skipping...');
      return 'denied';
    }

    this.isRequestingPermissions = true;

    try {
      if (Capacitor.isNativePlatform()) {
        // For mobile platforms, show a custom dialog first
        const result = await Dialog.confirm({
          title: 'Enable Notifications',
          message: 'TightShip needs notification permissions to keep you updated on important tasks and messages. This is essential for the app to work properly.',
          okButtonTitle: 'Allow',
          cancelButtonTitle: 'Not Now'
        });

        console.log('Dialog result:', result);

        if (result.value) {
          // User clicked "Allow", request permissions
          console.log('User clicked Allow, requesting native permissions...');
          try {
            const permission = await PushNotifications.requestPermissions();
            console.log('Native permission result:', permission);
            return mapNativeToAppPermission(permission.receive);
          } catch (error) {
            console.error('Error requesting permissions after dialog:', error);
            return 'denied';
          }
        } else {
          // User clicked "Not Now"
          console.log('User clicked Not Now, denying permissions');
          return 'denied';
        }
      } else {
        // For web platform, we can't customize the browser dialog
        // But we can show a custom message before requesting
        if ('Notification' in window) {
          // Show a custom alert explaining why notifications are needed
          const userConfirmed = confirm(
            'TightShip needs notification permissions to keep you updated on important tasks and messages. This is essential for the app to work properly.\n\nClick OK to allow notifications, or Cancel to skip.'
          );
          
          if (userConfirmed) {
            return await Notification.requestPermission();
          } else {
            return 'denied';
          }
        }
        return 'denied';
      }
    } finally {
      this.isRequestingPermissions = false;
    }
  }

  // Register a callback to be called when a message is received in foreground
  onMessageReceived(callback: (data: any) => void): () => void {
    this.onMessageReceivedCallbacks.push(callback);
    
    // Return unsubscribe function
    return () => {
      const index = this.onMessageReceivedCallbacks.indexOf(callback);
      if (index > -1) {
        this.onMessageReceivedCallbacks.splice(index, 1);
      }
    };
  }

  // Notify all registered callbacks when a message is received
  private notifyMessageReceived(data: any): void {
    this.onMessageReceivedCallbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in message received callback:', error);
      }
    });
  }

  // Reset all notification refresh hook states (called during logout)
  resetNotificationRefreshHooks(): void {
    console.log('Notifications: Resetting notification refresh hook states');
    // This will be called by the hooks to reset their registration state
    // The hooks will check this flag and reset their isRegistered state
    this.onMessageReceivedCallbacks = [];
  }
}

export const notificationService = new NotificationService();
